import type { PaymentResponse, PaymentIntentResponse, PayerResponse } from '../../api/models/bff/Payment';
import type { PaymentAuthenticationResponse } from '../../api/models/bff/PaymentAuthentication';
import { PaymentAuthenticationProviderType } from '../../models/payment/PaymentProvider';
import { generateId } from '../utils/generators';

/**
 * Create mock payment response
 *
 * @param overrides - Partial overrides for the payment response
 * @returns Mock payment response
 */
export function createMockPaymentResponse(overrides?: Partial<PaymentResponse>): PaymentResponse {
  return {
    id: 'pay_123456789',
    amount: '99.99',
    currency: 'EUR',
    status: 'AUTHORIZED',
    recurrentToken: 'tok_recurring_abc123',
    paymentMethod: {
      type: 'card',
      details: {
        holderName: 'John Doe',
        last4: '4242',
        expiry: {
          month: 12,
          year: 2028,
        },
        brand: 'visa',
      },
    },
    threeDsUrl: '',
    providerData: {
      transactionId: 'txn_987654321',
      processorResponseCode: '00',
    },
    ...overrides,
  };
}

/**
 * Create mock payment intent response with ECPay redirect data
 *
 * @param overrides - Partial overrides for the payment intent response
 * @returns Mock payment intent response
 */
export function createMockPaymentIntentResponse(overrides?: Partial<PaymentIntentResponse>): PaymentIntentResponse {
  return {
    id: 'sQTcFaOIl8RXy2zp47gR',
    amount: '500',
    currency: 'TWD',
    status: 'CREATED',
    providerData: {
      actionUrl: 'https://payment-stage.ecpay.com.tw/Binding/AddCreditCard',
      platformId: '0',
      merchantId: '3002607',
      merchantMemberId: '56daab8ed1cd4c7ba6d217bf244383',
      merchantTradeNo: '6PXrxL98q1Ytpea82P7d',
      tradeNo: '20260312164328',
      serverReplyUrl: 'https://ADD-TODO',
      clientRedirectUrl: `${window.location.origin}/payment?payment_redirected=true`,
      newAi: '1',
      timeStamp: '1773306088',
      checkMacValue: 'C3918984B3CE40E82E199D66520D4F3EB0F1D029A7E8BC39747133CF3D29C85C',
    },
    createdAt: '2026-03-12T08:43:28Z',
    ...overrides,
  };
}

/**
 * Create mock payer response
 *
 * @param overrides - Partial overrides for the payer response
 * @returns Mock payer response
 */
export function createMockPayerResponse(overrides?: Partial<PayerResponse>): PayerResponse {
  return {
    id: 'payer_123456789',
    provider: 'ECPAY',
    createdAt: new Date().toISOString(),
    identityId: 'identity_123',
    ...overrides,
  };
}

/**
 * Create mock payment authentication (3-D Secure) response.
 * Defaults to a freshly created session: PENDING, device data not collected yet, inert DDC iframe.
 *
 * @param overrides - Partial overrides for the authentication response
 * @returns Mock payment authentication response
 */
export function createMockPaymentAuthenticationResponse(overrides?: Partial<PaymentAuthenticationResponse>): PaymentAuthenticationResponse {
  return {
    id: generateId('auth'),
    provider: PaymentAuthenticationProviderType.TRIDENT_CARDINAL,
    status: 'PENDING',
    failureReason: null,
    providerData: {
      ddcIframeUrl: 'https://ddc.mock.invalid/collect',
      ddcCollected: false,
      ddcExpired: false,
    },
    ...overrides,
  };
}
